/**
 * Focus watchdog (docs/04 §2.1, QA3 T1).
 *
 * A D-pad app has exactly one contract: **something is always focused**. Norigin keeps the key
 * of an unmounted element as "current", so the moment a focused element goes away — a list
 * narrowed by a keystroke, an overlay that closed, a row re-rendered by an async response — the
 * remote is dead: arrows resolve against a node that no longer exists and nothing moves. That is
 * what "the arrows stopped working and focus disappeared" was.
 *
 * This module is the diagnosis half and is deliberately pure: it takes a snapshot of what
 * norigin and the DOM say and decides whether focus is healthy and, if not, where to put it.
 * `useFocusWatchdog` is the React half that takes the snapshots and applies the repair.
 *
 * ### Why the DOM is consulted at all
 * The TV initialises norigin with `shouldFocusDOMNode: false` (native DOM focus would scroll
 * the fixed 1920×1080 stage and fight the transform-based rows), so `document.activeElement`
 * stays on `<body>` **by design** — it is not the source of truth and can never be one here.
 * The managed equivalent is the marker every focusable renders: `data-focus-key` identifies it
 * and `data-focused="true"` says it believes it is the focused one. "Exactly one managed
 * focusable is active" is therefore a DOM-checkable invariant, which is also what the Playwright
 * spec asserts after every arrow key.
 */

/** Attribute every managed focusable leaf renders, carrying its norigin focus key. */
export const FOCUS_KEY_ATTR = 'data-focus-key';
/** Attribute a focused managed focusable renders (`"true"`). */
export const FOCUSED_ATTR = 'data-focused';
/**
 * Marks a modal/overlay subtree (dialog, popover, drawer). While one is mounted the focus
 * belongs inside it, so repairs must not hand the remote to a card behind the scrim.
 */
export const FOCUS_SCOPE_ATTR = 'data-focus-scope';
/**
 * Optional companion of {@link FOCUS_SCOPE_ATTR}: the key an overlay wants focused when it has
 * to be repaired — "Reproducir ahora" in the song dialog (QA3 T3), the current tone chip in the
 * popover. Without it a repair would land on whatever comes first in the DOM, which in the song
 * dialog is the `-2` tone chip.
 */
export const FOCUS_DEFAULT_ATTR = 'data-focus-default';
/**
 * Marks app chrome that is focusable but is not *content* — the rail. A repair may land there
 * only if the screen has nothing else: focusing the rail expands it over the content, which is
 * a state the user never asked for (docs/04 §7.1).
 */
export const FOCUS_CHROME_ATTR = 'data-focus-chrome';

/** Norigin's synthetic root: "current" before anything has ever been focused. */
export const ROOT_FOCUS_KEY = 'SN:ROOT';

export type FocusFault =
  | 'none'
  /** Nothing focusable is rendered — a boot/loading screen. Not a fault. */
  | 'empty'
  /** Norigin has no current key, or still points at its synthetic root. */
  | 'no_focus'
  /** The current key belongs to a focusable that has been unmounted. */
  | 'unmounted'
  /** The current key is outside the overlay that owns the screen (or is a bare container). */
  | 'out_of_scope'
  /** No managed focusable reports itself focused. */
  | 'no_active_node'
  /** More than one does. */
  | 'multiple_active'
  /** Exactly one does, but it is not the one norigin calls current — a render left behind. */
  | 'stale_active'
  /** Key handling was paused and never resumed. */
  | 'paused';

export interface FocusSnapshot {
  /** `getCurrentFocusKey()`. */
  current: string | null;
  /** `doesFocusableExist(current)`. */
  exists: boolean;
  /** `isTvNavigationPaused()`. */
  paused: boolean;
  /** Managed focusable keys in DOM order, narrowed to the topmost focus scope. */
  candidates: string[];
  /** The subset of `candidates` that belongs to app chrome (the rail), ranked last. */
  chrome?: string[];
  /** Keys of the nodes that render `data-focused="true"`, in DOM order. */
  active: string[];
}

/** How a repair chose its target (docs/04 §2.1: last focused → first focusable → default). */
export type FocusRepairStrategy =
  | 'last'
  | 'preferred'
  | 'first'
  | 'default'
  /** The current key was fine; only the DOM disagreed, so focus was re-asserted on it. */
  | 'resync'
  | 'resume'
  | 'none';

export interface FocusRepair {
  screen: string;
  fault: FocusFault;
  from: string | null;
  to: string | null;
  strategy: FocusRepairStrategy;
}

/** Faults that only the DOM can see, and that a still-settling render can fake. */
export const DOM_ONLY_FAULTS: readonly FocusFault[] = [
  'no_active_node',
  'multiple_active',
  'stale_active',
];

export function isFault(fault: FocusFault): boolean {
  return fault !== 'none' && fault !== 'empty';
}

/** Verdict on one snapshot. Order matters: the most structural fault wins. */
export function diagnoseFocus(snapshot: FocusSnapshot): FocusFault {
  if (snapshot.paused) return 'paused';
  // Nothing is focusable yet (boot, a screen still loading): there is nothing to repair.
  if (snapshot.candidates.length === 0) return 'empty';
  const current = snapshot.current;
  if (!current || current === ROOT_FOCUS_KEY) return 'no_focus';
  if (!snapshot.exists) return 'unmounted';
  // A key that exists but is not among the candidates is either a bare container or a node
  // behind the open overlay — either way the D-pad has nothing useful to move from.
  if (!snapshot.candidates.includes(current)) return 'out_of_scope';
  if (snapshot.active.length === 0) return 'no_active_node';
  if (snapshot.active.length > 1) return 'multiple_active';
  // One node claims the focus, but not the one norigin moved it to: the highlight the room
  // sees and the element Enter would press are two different things.
  if (snapshot.active[0] !== current) return 'stale_active';
  return 'none';
}

/**
 * The managed node that still claims the focus although norigin has moved on — the second
 * highlight of UX-046. It matters because re-asserting the current key cannot clear it:
 * `setFocus(current)` short-circuits when the key is already current, and norigin only ever
 * blurs the node *it* believes is focused. The cure is to park the focus on the stale node for
 * one turn, which makes it the "old" one, and then bring the focus back.
 */
export function staleActiveKey(snapshot: FocusSnapshot): string | null {
  if (!snapshot.current) return null;
  return snapshot.active.find((key) => key !== snapshot.current) ?? null;
}

export interface FocusPreferences {
  /** The last key that was verified healthy on this screen. */
  last?: string | null;
  /** The open overlay's declared default, when one is open (`FOCUS_DEFAULT_ATTR`). */
  preferred?: string | null;
  /** The screen's declared default (`useScreenFocus`). */
  fallback?: string | null;
}

/**
 * Where to send the focus, in Carlos's order: the last focused element, else the first
 * focusable of the screen, else the screen's default — with the open overlay's own default
 * slotted in ahead of "first", because the first node of a dialog is rarely its primary action.
 * Only keys that are actually mounted *and* reachable (inside the topmost scope) are considered.
 */
export function pickFocusTarget(
  snapshot: FocusSnapshot,
  preferences: FocusPreferences = {},
): { target: string | null; strategy: FocusRepairStrategy } {
  const { candidates, current } = snapshot;
  const usable = (key: string | null | undefined): key is string =>
    typeof key === 'string' && key.length > 0 && key !== current && candidates.includes(key);

  if (usable(preferences.last)) return { target: preferences.last, strategy: 'last' };
  // An open overlay owns its own default; "first in the DOM" is only right for a screen.
  if (usable(preferences.preferred)) {
    return { target: preferences.preferred, strategy: 'preferred' };
  }
  // The rail comes first in the DOM but is not content: skip it unless it is all there is.
  const chrome = snapshot.chrome ?? [];
  const first = candidates.find((key) => !chrome.includes(key) && usable(key));
  if (first) return { target: first, strategy: 'first' };
  if (usable(preferences.fallback)) return { target: preferences.fallback, strategy: 'default' };
  const anywhere = candidates.find(usable);
  if (anywhere) return { target: anywhere, strategy: 'first' };
  return { target: null, strategy: 'none' };
}

/* ------------------------------------------------------------------ DOM probes */

function topmostScope(doc: Document | HTMLElement): Element | null {
  const scopes = doc.querySelectorAll(`[${FOCUS_SCOPE_ATTR}]`);
  // The last one in DOM order is the topmost overlay (a dialog opened over a drawer).
  return scopes.length > 0 ? scopes[scopes.length - 1]! : null;
}

function scopeRoot(doc: Document | HTMLElement): ParentNode {
  return topmostScope(doc) ?? doc;
}

/** The open overlay's preferred focusable, if it declared one. */
export function scopeDefaultFocusKey(doc: Document | HTMLElement): string | null {
  return topmostScope(doc)?.getAttribute(FOCUS_DEFAULT_ATTR) ?? null;
}

function keysOf(root: ParentNode, selector: string): string[] {
  const out: string[] = [];
  for (const node of root.querySelectorAll(selector)) {
    const key = node.getAttribute(FOCUS_KEY_ATTR);
    if (key) out.push(key);
  }
  return out;
}

/** Managed focusables in DOM order, narrowed to the topmost open overlay when there is one. */
export function managedFocusKeys(doc: Document | HTMLElement): string[] {
  return keysOf(scopeRoot(doc), `[${FOCUS_KEY_ATTR}]`);
}

/** Managed focusables that belong to app chrome (the rail), so a repair can rank them last. */
export function chromeFocusKeys(doc: Document | HTMLElement): string[] {
  return keysOf(scopeRoot(doc), `[${FOCUS_CHROME_ATTR}] [${FOCUS_KEY_ATTR}]`);
}

/**
 * Is the focusable with this key inside an open overlay (dialog, popover, drawer)?
 *
 * UX-340: the rail remembers "the content the D-pad left behind" so Right/Back can hand the
 * focus back to it. A key that belongs to an overlay is not content: it is unmounted the moment
 * the overlay closes, and a remembered one left the rail with a dead target — Back and
 * ArrowRight were consumed by the rail and moved nothing, which is exactly how Home stopped
 * answering the remote after the queue drawer had been opened from it.
 */
export function isInFocusScope(key: string, doc: Document | HTMLElement = document): boolean {
  for (const node of doc.querySelectorAll(`[${FOCUS_SCOPE_ATTR}] [${FOCUS_KEY_ATTR}]`)) {
    if (node.getAttribute(FOCUS_KEY_ATTR) === key) return true;
  }
  return false;
}

/** Managed focusables that report themselves focused, document-wide (duplicates matter). */
export function activeFocusKeys(doc: Document | HTMLElement): string[] {
  return keysOf(doc, `[${FOCUS_KEY_ATTR}][${FOCUSED_ATTR}="true"]`);
}

/* --------------------------------------------- the screen default registry */

let screenName = 'app';
let screenDefault: string | null = null;

/**
 * Published by `useScreenFocus` so the watchdog — which is mounted once in the shell — knows
 * which screen it is guarding and what that screen's default focusable is.
 */
export function setScreenFocusDefault(screen: string, defaultFocusKey: string | null): void {
  screenName = screen;
  screenDefault = defaultFocusKey;
}

export function getScreenFocusDefault(): { screen: string; defaultFocusKey: string | null } {
  return { screen: screenName, defaultFocusKey: screenDefault };
}
